import { useMemo } from 'react';

import { useGeographyTaxonomyTree } from '../../../../shared/selection-lists/hooks/useGeographyTaxonomyTree';
import type { GqlSelectionListKey } from '../../../../shared/selection-lists/types';
import { getGeographyBranchLabels } from '../../../../shared/selection-lists/utils/geographyTaxonomyLabels';
import { geoLevelsToSelectionListIds } from '../../../../shared/selection-lists/utils/geoTaxonomyLevels';
import type { TaxonomyLevelMetadata } from '../../../../shared/selection-lists/utils/taxonomyTree';
import {
  PreferenceScope,
  UserPreferenceKey,
  useUserPreferences,
} from '../../../../shared/user-preferences';
import {
  type GeographyTaxonomyPreference,
  METRO_SELECTION_LIST_IDS,
  MSA_GEOGRAPHY_TAXONOMY_ID,
  resolveGeographyPreference,
  SELECTION_LIST_IDS_BY_GEOGRAPHY_TAXONOMY_ID,
  usesStateBreakdownOverMsa,
} from '../../../../shared/user-preferences/utils/geography-preference';

/**
 * Level keys used before the taxonomy's tree lands.
 *
 * The filter engine derives a selection's `levelId` from its id DEPTH against
 * this array, so the fallback has to match the taxonomy actually selected — not
 * merely be non-empty. Falling back to metro for every taxonomy re-keys a saved
 * `state` selection (depth 2) as `msa` for the duration of the fetch, and a
 * request firing in that window sends a state id in the `msa` field.
 *
 * Keyed by taxonomy id so both known taxonomies resolve their real levels
 * immediately; an unrecognised taxonomy still has to wait for its tree, but its
 * levels are unknowable until then anyway.
 */
const FALLBACK_SELECTION_LIST_IDS_BY_TAXONOMY_ID =
  SELECTION_LIST_IDS_BY_GEOGRAPHY_TAXONOMY_ID;

export type GeographyTaxonomyState = {
  taxonomyId: number;
  /** Provenance only — the version the stored preference was chosen under. */
  version: string | undefined;
  levels: TaxonomyLevelMetadata[];
  selectionListIds: GqlSelectionListKey[];
  branchLabels: ReturnType<typeof getGeographyBranchLabels>;
  /**
   * The `geo_breakdown_state_over_msa` answer for this taxonomy — see
   * `usesStateBreakdownOverMsa`. Exposed here so consumers don't each import
   * and call it; consumers that already hold a taxonomy id should call the
   * function directly instead.
   */
  usesStateBreakdownOverMsa: boolean;
  fetching: boolean;
};

/**
 * Resolves the active geography taxonomy from the user preference and exposes
 * its levels. The one place geography level keys and labels come from — nothing
 * downstream should hardcode `['region','country','msa','city']`.
 */
export const useGeographyTaxonomy = ({
  /**
   * When false, coerce to the metro taxonomy regardless of the preference.
   * Pages that don't support state geography pass their support flag here
   * rather than coercing the result, so the tree that gets FETCHED matches the
   * levels that get rendered. The global preference is left untouched.
   */
  allowNonMetroTaxonomy = true,
  /**
   * The taxonomy a deeplink or saved view was authored under, read off
   * `FilterId.GEOGRAPHY_TAXONOMY`. Shadows the stored preference for this
   * render only — the preference itself is never written from here.
   */
  overrideTaxonomy,
}: {
  allowNonMetroTaxonomy?: boolean;
  overrideTaxonomy?: GeographyTaxonomyPreference;
} = {}): GeographyTaxonomyState => {
  const { preferences } = useUserPreferences(PreferenceScope.GLOBAL);

  const preference = resolveGeographyPreference(
    preferences[UserPreferenceKey.Geography]
  );
  const activeTaxonomy = overrideTaxonomy ?? preference;
  const taxonomyId = allowNonMetroTaxonomy
    ? activeTaxonomy.taxonomyId
    : MSA_GEOGRAPHY_TAXONOMY_ID;
  const version = allowNonMetroTaxonomy ? activeTaxonomy.version : undefined;

  // Deliberately NOT pinned to `preference.version`: the version stored when
  // the user last clicked can lag what the picker now advertises, leaving it
  // promising `State > County` while the filter still serves the pre-rename
  // `city_core` level.
  const { levels, fetching } = useGeographyTaxonomyTree({
    taxonomyId,
  });

  const selectionListIds = useMemo(() => {
    const derived = geoLevelsToSelectionListIds(levels);
    if (derived.length > 0) return derived;
    return (
      FALLBACK_SELECTION_LIST_IDS_BY_TAXONOMY_ID[taxonomyId] ??
      METRO_SELECTION_LIST_IDS
    );
  }, [levels, taxonomyId]);

  const branchLabels = useMemo(
    () => getGeographyBranchLabels(levels),
    [levels]
  );

  return {
    taxonomyId,
    version,
    levels,
    selectionListIds,
    branchLabels,
    usesStateBreakdownOverMsa: usesStateBreakdownOverMsa(taxonomyId),
    fetching,
  };
};
